import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import {
  fetchAdminFiles,
  uploadAdminFile as uploadFile,
  downloadAdminFile,
  deleteAdminFile,
  publishAdminFile,
  unpublishAdminFile,
} from '../services/adminFilesService';
import './DosyalarPage.css';

const MAX_FILE_SIZE_MB = 200;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  if (error?.status === 404) return 'API endpoint bulunamadı.';
  if (error?.status === 401) return 'Kimlik doğrulama hatası. Lütfen tekrar giriş yapın.';
  if (error?.status === 403) return 'Bu işlem için yetkiniz yok.';
  return (
    error?.message ||
    error?.data?.mesaj ||
    error?.data?.message ||
    error?.data?.error ||
    error?.data?.hata ||
    fallback
  );
};

const formatFileSize = (bytes) => {
  if (bytes == null || bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (value) => {
  if (!value) return '—';
  try {
    const date = typeof value === 'string' || typeof value === 'number'
      ? new Date(value)
      : value instanceof Date ? value : null;
    if (!date || isNaN(date.getTime())) return '—';
    return date.toLocaleString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const getFileIcon = (filename) => {
  const ext = (filename || '').split('.').pop()?.toLowerCase();
  if (ext === 'apk') return 'fa-brands fa-android';
  if (ext === 'pdf') return 'fa-file-pdf';
  if (['doc', 'docx'].includes(ext)) return 'fa-file-word';
  if (['xls', 'xlsx'].includes(ext)) return 'fa-file-excel';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'fa-file-image';
  if (['zip', 'rar', '7z'].includes(ext)) return 'fa-file-zipper';
  return 'fa-file-lines';
};

const DosyalarPage = () => {
  const { confirm } = useConfirmDialog();
  const [dosyalar, setDosyalar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [publishingId, setPublishingId] = useState(null);
  const [unpublishingId, setUnpublishingId] = useState(null);
  const [publishModal, setPublishModal] = useState(null);
  const fileInputRef = useRef(null);

  const loadFiles = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetchAdminFiles();
      const list = response?.veri?.dosyalar || [];
      setDosyalar(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(getErrorMessage(err, 'Dosyalar yüklenemedi'));
      setDosyalar([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setUploadError(`Dosya boyutu çok büyük. Maksimum ${MAX_FILE_SIZE_MB} MB yükleyebilirsiniz.`);
      return;
    }

    setUploading(true);
    setUploadError('');
    try {
      await uploadFile(file);
      if (window.showNotification) {
        window.showNotification('success', 'Dosya başarıyla yüklendi');
      }
      await loadFiles();
    } catch (err) {
      setUploadError(getErrorMessage(err, 'Dosya yüklenemedi'));
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(err, 'Dosya yüklenemedi'));
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (id, filename) => {
    setDownloadingId(id);
    try {
      await downloadAdminFile(id, filename || 'download');
    } catch (err) {
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(err, 'Dosya indirilemedi'));
      }
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (id, filename) => {
    const ok = await confirm({
      title: 'Dosyayı sil',
      message: `"${filename || 'Dosya'}" kalıcı olarak silinsin mi?`,
      variant: 'danger',
      confirmLabel: 'Evet, sil',
    });
    if (!ok) return;

    setDeletingId(id);
    try {
      await deleteAdminFile(id);
      if (window.showNotification) {
        window.showNotification('success', 'Dosya silindi');
      }
      if (publishModal?.id === id) {
        setPublishModal(null);
      }
      await loadFiles();
    } catch (err) {
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(err, 'Dosya silinemedi'));
      }
    } finally {
      setDeletingId(null);
    }
  };

  const handlePublish = async (id) => {
    setPublishingId(id);
    try {
      const response = await publishAdminFile(id);
      const dosya = response?.veri?.dosya;
      if (window.showNotification) {
        window.showNotification('success', 'Dosya yayınlandı');
      }
      await loadFiles();
      if (dosya?.publicUrl) {
        setPublishModal({
          id,
          filename: dosya.originalFilename,
          publicUrl: dosya.publicUrl,
        });
      }
    } catch (err) {
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(err, 'Dosya yayınlanamadı'));
      }
    } finally {
      setPublishingId(null);
    }
  };

  const handleUnpublish = async (id) => {
    setUnpublishingId(id);
    try {
      await unpublishAdminFile(id);
      if (window.showNotification) {
        window.showNotification('success', 'Yayın kaldırıldı');
      }
      setPublishModal(null);
      await loadFiles();
    } catch (err) {
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(err, 'Yayın kaldırılamadı'));
      }
    } finally {
      setUnpublishingId(null);
    }
  };

  const openPublishModal = (file) => {
    if (!file?.publicUrl) return;
    setPublishModal({
      id: file.id || file._id,
      filename: file.originalFilename || file.storedFilename,
      publicUrl: file.publicUrl,
    });
  };

  const closePublishModal = () => {
    setPublishModal(null);
  };

  const handleCopyPublicUrl = async () => {
    if (!publishModal?.publicUrl) return;
    try {
      await navigator.clipboard.writeText(publishModal.publicUrl);
      window.showNotification?.('success', 'Link kopyalandı');
    } catch {
      window.showNotification?.('error', 'Kopyalanamadı');
    }
  };

  const renderActions = (file, variant = 'table') => {
    const id = file.id || file._id;
    const filename = file.originalFilename || file.storedFilename || '—';
    const isPublished = Boolean(file.publishToken && file.publicUrl);
    const isDownloading = downloadingId === id;
    const isDeleting = deletingId === id;
    const isPublishing = publishingId === id;
    const btnClass = variant === 'card' ? 'dosyalar-card__btn' : 'dosyalar-action-btn';
    const downloadClass = `${btnClass} ${variant === 'card' ? 'dosyalar-card__btn--download' : 'dosyalar-action-btn--download'}`;
    const deleteClass = `${btnClass} ${variant === 'card' ? 'dosyalar-card__btn--delete' : 'dosyalar-action-btn--delete'}`;
    const publishClass = `${btnClass} ${variant === 'card' ? 'dosyalar-card__btn--publish' : 'dosyalar-action-btn--publish'}`;
    const infoClass = `${btnClass} dosyalar-action-btn--info`;

    const publishButton = isPublished ? null : (
      <button
        type="button"
        className={publishClass}
        title="Yayınla"
        onClick={() => handlePublish(id)}
        disabled={isPublishing}
      >
        {isPublishing ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-share-from-square" />}
        {variant === 'card' ? 'Yayınla' : null}
      </button>
    );

    const infoButton = isPublished ? (
      <button
        type="button"
        className={infoClass}
        title="Yayın linki"
        onClick={() => openPublishModal(file)}
      >
        <i className="fas fa-circle-info" />
      </button>
    ) : null;

    if (variant === 'card') {
      return (
        <>
          {publishButton}
          {infoButton}
          <button
            type="button"
            className={downloadClass}
            onClick={() => handleDownload(id, filename)}
            disabled={isDownloading}
          >
            {isDownloading ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-download" />}
            İndir
          </button>
          <button
            type="button"
            className={deleteClass}
            onClick={() => handleDelete(id, filename)}
            disabled={isDeleting}
          >
            {isDeleting ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-trash-alt" />}
            Kaldır
          </button>
        </>
      );
    }

    return (
      <>
        {publishButton}
        {infoButton}
        <button
          type="button"
          className={downloadClass}
          title="İndir"
          onClick={() => handleDownload(id, filename)}
          disabled={isDownloading}
        >
          {isDownloading ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-download" />}
        </button>
        <button
          type="button"
          className={deleteClass}
          title="Kaldır"
          onClick={() => handleDelete(id, filename)}
          disabled={isDeleting}
        >
          {isDeleting ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-trash-alt" />}
        </button>
      </>
    );
  };

  return (
    <div className="page-container dosyalar-page">
      <div className="page-header dosyalar-page__header">
        <h1>Dosyalar</h1>
        <div className="dosyalar-page__upload">
          <button
            type="button"
            className="primary-btn"
            onClick={handleUploadClick}
            disabled={uploading}
          >
            {uploading ? (
              <>
                <i className="fas fa-spinner fa-spin" />
                Yükleniyor...
              </>
            ) : (
              <>
                <i className="fas fa-upload" />
                Dosya Yükle
              </>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            onChange={handleFileChange}
            accept="*/*"
          />
        </div>
      </div>

      {uploadError && <div className="dosyalar-page__alert">{uploadError}</div>}
      {error && <div className="dosyalar-page__alert">{error}</div>}

      {loading ? (
        <div className="dosyalar-page__loading">
          <i className="fas fa-spinner fa-spin" />
          Yükleniyor...
        </div>
      ) : dosyalar.length === 0 ? (
        <div className="dosyalar-page__panel dosyalar-page__empty">
          <div className="dosyalar-page__empty-icon">
            <i className="fas fa-folder-open" />
          </div>
          Henüz dosya yüklenmemiş. Yüklemek için &quot;Dosya Yükle&quot; butonunu kullanın.
        </div>
      ) : (
        <>
          <div className="dosyalar-page__table-wrap table-container">
            <table className="admins-table dosyalar-table">
              <thead>
                <tr>
                  <th>Dosya Adı</th>
                  <th>Boyut</th>
                  <th>Yüklenme Tarihi</th>
                  <th className="islemler-sutunu">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {dosyalar.map((d) => (
                    <tr key={d.id || d._id}>
                      <td>
                        <div className="dosyalar-table__name">
                          <span className="dosyalar-table__icon" aria-hidden="true">
                            <i className={`fas ${getFileIcon(d.originalFilename || d.storedFilename)}`} />
                          </span>
                          <span className="dosyalar-table__filename">{d.originalFilename || d.storedFilename || '—'}</span>
                        </div>
                      </td>
                      <td className="dosyalar-table__meta">{formatFileSize(d.size)}</td>
                      <td className="dosyalar-table__meta">{formatDate(d.uploadedAt)}</td>
                      <td className="islemler-sutunu">
                        <div className="dosyalar-table__actions">
                          {renderActions(d, 'table')}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <div className="dosyalar-page__cards">
            {dosyalar.map((d) => (
                <article key={d.id || d._id} className="dosyalar-card">
                  <div className="dosyalar-card__main">
                    <div className="dosyalar-card__icon" aria-hidden="true">
                      <i className={`fas ${getFileIcon(d.originalFilename || d.storedFilename)}`} />
                    </div>
                    <div className="dosyalar-card__info">
                      <h2 className="dosyalar-card__name">{d.originalFilename || d.storedFilename || '—'}</h2>
                      <div className="dosyalar-card__meta">
                        <span className="dosyalar-card__meta-item">
                          <i className="fas fa-weight-hanging" />
                          {formatFileSize(d.size)}
                        </span>
                        <span className="dosyalar-card__meta-item">
                          <i className="fas fa-clock" />
                          {formatDate(d.uploadedAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="dosyalar-card__actions">
                    {renderActions(d, 'card')}
                  </div>
                </article>
              ))}
          </div>
        </>
      )}

      {publishModal && (
        <div
          className="modal-overlay dosyalar-publish-modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) closePublishModal();
          }}
        >
          <div className="dosyalar-publish-modal">
            <div className="dosyalar-publish-modal__header">
              <h2>Yayın linki</h2>
              <button type="button" className="dosyalar-publish-modal__close" onClick={closePublishModal} aria-label="Kapat">
                <i className="fas fa-times" />
              </button>
            </div>
            <p className="dosyalar-publish-modal__filename">{publishModal.filename}</p>
            <div className="dosyalar-publish-modal__url">{publishModal.publicUrl}</div>
            <div className="dosyalar-publish-modal__actions">
              <button type="button" className="primary-btn" onClick={handleCopyPublicUrl}>
                <i className="fas fa-copy" />
                URL&apos;yi kopyala
              </button>
              <button
                type="button"
                className="secondary-btn dosyalar-publish-modal__unpublish"
                onClick={() => handleUnpublish(publishModal.id)}
                disabled={unpublishingId === publishModal.id}
              >
                {unpublishingId === publishModal.id ? (
                  <i className="fas fa-spinner fa-spin" />
                ) : (
                  <i className="fas fa-link-slash" />
                )}
                Yayından kaldır
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DosyalarPage;
